/* tslint:disable:cyclomatic-complexity */
import { CountWithTitle, Status, TextLink } from '@mm/components';
import { CampaignType } from '@mm/state/model/CampaignsModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { UICampaignItem } from '@mm/state/reducers/campaignsOverview';
import { getCampaignDisplayStatus } from '@mm/state/selectors/campaignSelectors';
import {
  activeChatbotIdSelector,
  chatbotIsActiveOnConnectedPageSelector,
} from '@mm/state/selectors/viewSelectors';
import { ReactComponent as MessengerIcon } from 'assets/icons/messenger-fb.svg';
import { ReactComponent as SMSIcon } from 'assets/icons/sms-icon.svg';
import { ReactComponent as InstagramIcon } from 'assets/instagram.svg';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import {
  ThreeDotMenu,
  MenuItem,
} from '../../../../../../../common/components/ThreeDotMenu/ThreeDotMenu';
import * as routes from '../../../../../../../common/constants/routes';
import { formatAcceptableTime } from '../../../../../../../common/helpers/date-formatting';
import { formatDateTime } from '../../../../../../../common/helpers/formatDateTime';
import { trackWebAppGAEvent } from '../../../../../../../common/helpers/plugins/website-google-analytics';
import { BotBuilderRoutes } from '../../../../../../../routes/bot-builder-routes';
import { useShowInactiveBannerFlag } from '../../../../../../common/hooks/use-show-inactive-bot-banner-flag';
import { UIFilterCampaignKindType } from '../../types';
import { useDeleteCampaign } from '../../use-delete-campaign';
import { EntityRowBaseTemplate } from '../EntityRowBaseTemplate';
import { CampaignBaseInfo, Kind } from '../SharedCells';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { useCampaignsOverviewCRUDActions } from '@mm/state/actions/CampaignsOverviewCRUDActions';

export const CampaignRow = ({
  item,
  kind,
  fetchCurrentActiveKind,
}: {
  item: UICampaignItem;
  kind: UIFilterCampaignKindType;
  fetchCurrentActiveKind: () => void;
}) => {
  const {
    id,
    name,
    audienceName,
    platforms = [],
    deliveredCount,
    smsDeliveredCount,
    respondedCount,
    smsRespondedCount,
    instagramRespondedCount,
    instagramDeliveredCount,
    sentAt,
    queuedAt,
    parentId,
  } = item;

  const { history } = useRouter();
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const chatbotIsActiveOnConnectedPage = useSelector(
    chatbotIsActiveOnConnectedPageSelector,
  );
  const modalActions = useModalActions();
  const { onClickActivate } = useShowInactiveBannerFlag();
  const { retrieveAll } = useCampaignsOverviewCRUDActions();

  const queuedDateTime = queuedAt && formatDateTime(queuedAt);
  const sentDateTime = sentAt && formatDateTime(sentAt);

  const subHeadingText = audienceName && `Audience: ${audienceName}`;

  const bottomText =
    item.acceptableTime &&
    `Schedule: ${formatAcceptableTime(item.acceptableTime)}`;

  const displayStatus = getCampaignDisplayStatus(item as CampaignType);

  const isDrip = kind === 'drip_campaigns';
  const isBlastDraft = !isDrip && !item.active;
  const isImmediateBlast = !isDrip && item.scheduleType === 'none';

  const isCampaignSending =
    item.active &&
    (displayStatus === 'Sending' ||
      displayStatus === 'Queued' ||
      displayStatus === 'Active');

  const dataByPlatform = {
    facebook: {
      icon: MessengerIcon,
      sent: deliveredCount,
      responded: respondedCount,
    },
    sms: {
      icon: SMSIcon,
      sent: smsDeliveredCount,
      responded: smsRespondedCount,
    },
    instagram: {
      icon: InstagramIcon,
      sent: instagramDeliveredCount || 0,
      responded: instagramRespondedCount,
    },
  };

  const defaultStatsInfo = { icons: [], sent: '', responded: '' };

  const statsInfo = platforms.reduce((a, platformType, i) => {
    const data = dataByPlatform[platformType];
    if (!data) return a;

    const { sent, responded, icon: Icon } = data;
    const isLast = platforms.length === i + 1;

    const platformIcon = (
      <>
        <Icon />
        {!isLast && (
          <TextLink className="campaign-row__platforms-divider">/</TextLink>
        )}
      </>
    );

    const sentCountFormatted = `${sent}${isLast ? '' : '/'}`;

    const respondedCountFormatted = `${responded}${isLast ? '' : '/'}`;

    return {
      icons: [...a.icons, platformIcon],
      sent: a.sent + sentCountFormatted,
      responded: a.responded + respondedCountFormatted,
    };
  }, defaultStatsInfo);

  const campaignReportPath = routes.CHATBOT_EDITOR_CAMPAIGN_REPORT(
    activeChatbotId,
    id,
  );

  const navigateToEditRoute = () => {
    const opts = {
      chatbotId: activeChatbotId,
      campaignId: id,
    };

    const path = isDrip
      ? BotBuilderRoutes.dripCampaignEdit.getLink(opts)
      : isBlastDraft
      ? BotBuilderRoutes.chatBlastEdit.getLink(opts)
      : campaignReportPath;

    history.push(path);
  };

  const onRowClick = () => {
    const label = isDrip ? `table_item_drip` : `table_item_blast`;
    trackWebAppGAEvent('click', { category: 'campaigns_table', label });
    navigateToEditRoute();
  };

  const campaignsCRUDActions = useCampaignCRUDActions();

  const handleDripClone = useCallback(async () => {
    await campaignsCRUDActions.clone(id);
    retrieveAll({
      botId: activeChatbotId,
      kind: 'drip',
    });
  }, [id]);

  const { deleteCampaign, updateCampaign } = useDeleteCampaign();

  const onSelectItem = (menuItem: string) => {
    switch (menuItem) {
      case 'edit':
        const label = isDrip ? `edit_button_drip` : `edit_button_blast`;
        trackWebAppGAEvent('click', { category: 'campaigns_table', label });
        return navigateToEditRoute();
      case 'report':
        return history.push(campaignReportPath);
      case 'clone':
        handleDripClone();
        return '';
      case 'delete':
        return deleteCampaign(item);
      case 'cancel':
        return updateCampaign(
          {
            id: item.id,
            status: 'canceled',
          } as CampaignType,
          fetchCurrentActiveKind,
        );
      case 'pause': {
        const updatedItem = {
          id: item.id,
          status: 'paused',
        };
        return updateCampaign(
          updatedItem as CampaignType,
          fetchCurrentActiveKind,
        );
      }
      case 'resume': {
        const updatedItem = {
          id: item.id,
          status: 'queued',
        };
        const updateAction = () =>
          updateCampaign(updatedItem as CampaignType, fetchCurrentActiveKind);
        if (!chatbotIsActiveOnConnectedPage) {
          modalActions.openActivateInactiveBotModal({
            onBotActivateConfirm: () =>
              setTimeout(() => onClickActivate(updateAction), 500),
            useCustomOnBotActivateConfirm: true,
          });
        } else {
          updateAction();
        }
        break;
      }
      default:
        break;
    }
  };
  // Can delete either draft drip or not delivered chat blaster
  const showDeleteButton =
    !item.active || ['Ended', 'Delivered'].includes(displayStatus);

  const showPauseButton = !isImmediateBlast && isCampaignSending;
  const showCancelButton = isImmediateBlast && isCampaignSending;

  const showResumeButton = displayStatus === 'Paused';

  // Can edit any drip or not sent blaster
  const showEditButton = isDrip || isBlastDraft;
  // Can view reports for delivered blaster
  const showReportButton = !isDrip && !isBlastDraft;

  const showCloneButton = isDrip;

  const menuElements = [
    showReportButton && { name: 'View report', value: 'report' },
    showEditButton && { name: 'Edit', value: 'edit' },
    showPauseButton && { name: 'Pause', value: 'pause' },
    showCancelButton && { name: 'Cancel', value: 'cancel' },
    showResumeButton && { name: 'Resume', value: 'resume' },
    showCloneButton && { name: 'Clone', value: 'clone' },
    showDeleteButton && { name: 'Delete', value: 'delete' },
  ].filter(Boolean);

  const onMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const status = getCampaignDisplayStatus(item as CampaignType);

  if (parentId) {
    return null;
  }

  return (
    <>
      <EntityRowBaseTemplate
        className="starters-table__data-row starters-table__data-row--large"
        onClick={onRowClick}
        inactive={status === 'Draft'}
      >
        <CampaignBaseInfo
          headingText={name}
          subHeadingText={subHeadingText}
          bottomText={bottomText}
        />

        <Kind kind={kind} />

        <TextLink>
          {(sentDateTime || queuedDateTime) && (
            <span>Created date: {sentDateTime || queuedDateTime}</span>
          )}
        </TextLink>

        <Status status={status} />

        <div className="campaign-row__platforms-cell">{statsInfo?.icons}</div>

        <CountWithTitle count={statsInfo?.sent} text="Sent" isActive />

        <CountWithTitle
          count={statsInfo?.responded}
          text="Responded"
          isActive
        />

        <ThreeDotMenu
          className="shared_cells__menu"
          onClick={onMenuClick}
          elements={menuElements as MenuItem[]}
          selectItem={onSelectItem}
        />
      </EntityRowBaseTemplate>
    </>
  );
};
